import React, { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/auth-store";
import { LoginModal } from "../components/UI/LoginModal";

interface CommonLayoutProps {
  children: React.ReactNode;
  showAuth?: boolean;
}

export const Pod24CommonLayout: React.FC<CommonLayoutProps> = ({
  children,
  showAuth = true,
}) => {
  const location = useLocation();

  // Disable browser auto scroll restoration once on mount
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "auto";
    }
  }, []);

  // Run before paint so scroll position is set instantly
  useLayoutEffect(() => {
    // include pathname/search/hash so more types of navigation trigger this
    window.scrollTo(0, 0); // instant, no animation
  }, [location.pathname, location.search, location.hash]);

  const navigate = useNavigate();
  const { user, logout, isAuthenticated, canAccessCompany, isAdmin } =
    useAuthStore();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    navigate("/");
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-normal font-['Pretendard']">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 h-20">
        <div
          className="flex items-center h-full"
          style={{
            paddingLeft: "25px",
            paddingRight: "25px",
            paddingTop: "23px",
            paddingBottom: "23px",
          }}
        >
          <div
            className="font-bold font-['Pretendard'] text-lg md:text-xl lg:text-[28px] lg:w-[97px] lg:h-[34px] lg:flex lg:items-center cursor-pointer"
            style={{ color: "#4285F4" }}
            onClick={() => navigate("/")}
          >
            POD24
          </div>

          {/* Desktop Navigation */}
          <nav
            className="hidden md:flex items-center text-zinc-800 font-medium font-['Pretendard'] h-[40px] flex-1 justify-center"
            style={{
              gap: "30px",
              fontSize: "16px",
              marginLeft: "clamp(20px, 8vw, 639px)",
              marginRight: "clamp(20px, 8vw, 639px)",
            }}
          >
            <button
              onClick={() => scrollToSection("home")}
              className="hover:text-[#2c5aa0] transition-colors cursor-pointer whitespace-nowrap"
            >
              홈
            </button>
            <button
              onClick={() => scrollToSection("services")}
              className="hover:text-[#2c5aa0] transition-colors cursor-pointer whitespace-nowrap"
            >
              서비스소개
            </button>
            <button
              onClick={() => scrollToSection("cases")}
              className="hover:text-[#2c5aa0] transition-colors cursor-pointer whitespace-nowrap"
            >
              구축사례
            </button>
          </nav>

          {/* Auth Buttons */}
          {showAuth && (
            <div
              className="flex w-full md:w-auto justify-end"
              style={{
                gap: "28px",
                paddingTop: "30.5px",
                paddingBottom: "30.5px",
              }}
            >
              {isAuthenticated ? (
                <>
                  <button
                    onClick={handleLogout}
                    className="font-normal font-['Pretendard'] hover:text-blue-800 transition-colors whitespace-nowrap"
                    style={{ fontSize: "16px", color: "#252525" }}
                  >
                    로그아웃
                  </button>
                  {canAccessCompany() ? (
                    <button
                      onClick={() => navigate(`/company/${user?.companyId}`)}
                      className="font-normal font-['Pretendard'] hover:text-blue-800 transition-colors whitespace-nowrap"
                      style={{ fontSize: "16px", color: "#252525" }}
                    >
                      관리자페이지
                    </button>
                  ) : null}
                  {isAdmin() ? (
                    <button
                      onClick={() => navigate("/admin")}
                      className="font-normal font-['Pretendard'] hover:text-blue-800 transition-colors whitespace-nowrap"
                      style={{ fontSize: "16px", color: "#252525" }}
                    >
                      POD24-관리자페이지
                    </button>
                  ) : null}
                  {!canAccessCompany() && !isAdmin() ? (
                    <button
                      onClick={() => {
                        navigate("/company/apply");
                      }}
                      className="font-normal font-['Pretendard'] hover:text-blue-800 transition-colors whitespace-nowrap"
                      style={{ fontSize: "16px", color: "#252525" }}
                    >
                      신청하기
                    </button>
                  ) : null}
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsLoginModalOpen(true)}
                    className="text-md font-normal font-['Pretendard'] hover:text-blue-800 transition-colors whitespace-nowrap"
                    style={{ fontSize: "16px", color: "#252525" }}
                  >
                    로그인
                  </button>
                  <button
                    onClick={() => navigate("/signup")}
                    className="text-md font-normal font-['Pretendard'] hover:text-blue-800 transition-colors whitespace-nowrap"
                    style={{ color: "#252525" }}
                  >
                    회원가입
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20">{children}</main>

      {/* Footer */}
      <footer className="bg-gray-100 py-8 md:py-20 ">
        <div className="max-w-screen-xl mx-auto px-5 md:px-20">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 md:gap-16">
            {/* 이용안내 */}
            <div className="text-left w-full md:w-auto">
              <h3 className="font-['Pretendard'] font-medium text-gray-900 text-left text-base md:text-lg mb-1 md:mb-[9px]">
                이용안내
              </h3>
              <div className="text-left">
                <p className="font-['Pretendard'] font-bold text-gray-900 text-left text-xl md:text-2xl mb-6 md:mb-[30px]">
                  010-3324-0487
                </p>
                <p className="font-['Pretendard'] font-medium text-gray-700 text-left text-sm md:text-[14px] mb-1">
                  평일 09:00~18:00
                </p>
                <p className="font-['Pretendard'] font-medium text-gray-700 text-left text-sm md:text-[14px]">
                  점심 12:00~13:00 (주말, 공휴일은 제외)
                </p>
              </div>
            </div>

            {/* 회사 정보 */}
            <div className="text-left w-full md:w-auto">
              <div className="flex flex-wrap mb-43 md:mb-6 text-left gap-4 md:gap-[35px]">
                <a
                  href="/#"
                  onClick={() => scrollToSection("home")}
                  className="font-['Pretendard'] font-medium text-gray-700 hover:text-gray-900 text-sm md:text-[15px]"
                >
                  서비스소개
                </a>
                <a
                  href="/terms"
                  className="font-['Pretendard'] font-medium text-gray-700 hover:text-gray-900 text-sm md:text-[15px]"
                >
                  이용약관
                </a>
                <a
                  href="/privacy"
                  className="font-['Pretendard'] font-medium text-gray-700 hover:text-gray-900 text-sm md:text-[15px]"
                >
                  개인정보보호정책
                </a>
              </div>
              <div className="text-gray-600 text-left mt-6 md:mt-[45px]">
                <p className="font-['Pretendard'] font-medium text-left text-xs md:text-[14px] mb-1 md:mb-2">
                  (주) 데이토즈 / 대표: 김은아 / 개인정보 관리 책임자: 김은아
                </p>
                <p className="font-['Pretendard'] font-medium text-left text-xs md:text-[14px] mb-1 md:mb-2">
                  주소: 서울 금천구 디지털로9길 99 스타밸리, 805호
                </p>
                <p className="font-['Pretendard'] font-medium text-left text-xs md:text-[14px]">
                  사업자등록번호: 637-81-01964 / 통신판매신고번호: 제
                  2024-서울금천-1792호
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </div>
  );
};
